import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { Menu, X, ExternalLink, ChevronDown, BarChart3, Wallet } from "lucide-react";
import { SITE, isCaLive } from "@/lib/config";
import { CA_NOT_LIVE_TOAST } from "@/lib/demo-data";
import { useWalletConnect, truncateAddr } from "@/lib/wallet";
import { cn } from "@/lib/utils";
import { LogoLockup } from "./Logo";
import { MonoLabel, StatusDot, XIcon, btnOutline, btnPrimary } from "./primitives";

const LINKS = [
  { to: "/protocol", label: "Protocol" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/outcomes", label: "Outcomes" },
  { to: "/reputation", label: "Reputation" },
  { to: "/terminal", label: "Terminal" },
  { to: "/docs", label: "Docs" },
];

const MORE = [
  { to: "/ecosystem", label: "Ecosystem" },
  { to: "/token", label: "$CONSEQ" },
  { to: "/manifesto", label: "Manifesto" },
];

export function Navbar() {
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();
  const { address, connect } = useWalletConnect();

  useEffect(() => {
    setOpen(false);
    setMoreOpen(false);
  }, [location.pathname]);

  const onConnect = () => {
    if (!isCaLive) {
      toast(CA_NOT_LIVE_TOAST, { description: "Contract address will be available at launch." });
      return;
    }
    connect();
  };

  const connectLabel = address ? truncateAddr(address) : isCaLive ? "Connect Wallet" : "CA Coming Soon";

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-line bg-base/85 backdrop-blur-xl">
      <div className="container-t flex h-16 items-center justify-between gap-3">
        <Link to="/" aria-label="Consequence Protocol — home" data-testid="nav-brand-logo" className="shrink-0">
          <LogoLockup />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-5 whitespace-nowrap lg:flex" aria-label="Primary">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                cn(
                  "font-mono text-[10px] font-medium uppercase tracking-[0.14em] transition-colors duration-200",
                  isActive ? "text-cyan" : "text-dim hover:text-ink"
                )
              }
            >
              {l.label}
            </NavLink>
          ))}

          <div className="relative" onMouseEnter={() => setMoreOpen(true)} onMouseLeave={() => setMoreOpen(false)}>
            <button
              data-testid="nav-more-toggle"
              aria-expanded={moreOpen}
              onClick={() => setMoreOpen((v) => !v)}
              className="flex items-center gap-1 font-mono text-[10px] font-medium uppercase tracking-[0.14em] text-dim transition-colors duration-200 hover:text-ink"
            >
              More <ChevronDown className="h-3 w-3" aria-hidden="true" />
            </button>
            <AnimatePresence>
              {moreOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 6 }}
                  transition={{ duration: 0.15 }}
                  className="panel absolute right-0 top-full z-10 w-44 py-2 shadow-2xl"
                >
                  {MORE.map((m) => (
                    <NavLink
                      key={m.to}
                      to={m.to}
                      data-testid={`nav-link-${m.label.replace("$", "").toLowerCase()}`}
                      className={({ isActive }) =>
                        cn(
                          "block px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] transition-colors duration-150",
                          isActive ? "bg-cyan/10 text-cyan" : "text-dim hover:bg-white/5 hover:text-ink"
                        )
                      }
                    >
                      {m.label}
                    </NavLink>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </nav>

        {/* Right cluster */}
        <div className="hidden shrink-0 items-center gap-2 lg:flex">
          <a
            href={SITE.xUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Consequence Protocol on X"
            data-testid="nav-x-link"
            className="flex h-8 w-8 items-center justify-center border border-line text-dim transition-colors duration-200 hover:border-cyan/50 hover:text-cyan"
          >
            <XIcon />
          </a>
          <a href={SITE.chartUrl} target="_blank" rel="noopener noreferrer" data-testid="nav-chart-btn" className={cn(btnOutline, "px-3 py-2")}>
            <BarChart3 className="h-3.5 w-3.5" aria-hidden="true" />
            Chart
          </a>
          <a href={SITE.buyUrl} target="_blank" rel="noopener noreferrer" data-testid="nav-buy-btn" className={cn(btnOutline, "px-3 py-2")}>
            <Wallet className="h-3.5 w-3.5" aria-hidden="true" />
            Buy
          </a>
          <button
            data-testid="nav-connect-btn"
            onClick={onConnect}
            title={address ? address : isCaLive ? "Connect wallet" : "Contract address will be available at launch."}
            className={cn(btnPrimary, "px-3 py-2")}
          >
            {connectLabel}
          </button>
        </div>

        {/* Mobile toggle */}
        <button
          className="flex h-9 w-9 items-center justify-center border border-line text-ink lg:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          data-testid="nav-mobile-toggle"
        >
          {open ? <X className="h-4 w-4" aria-hidden="true" /> : <Menu className="h-4 w-4" aria-hidden="true" />}
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-line bg-base lg:hidden"
            aria-label="Mobile"
            data-testid="nav-mobile-menu"
          >
            <div className="container-t flex flex-col gap-1 py-4">
              {[...LINKS, ...MORE].map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-mobile-link-${l.label.replace("$", "").toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center justify-between border-b border-line/60 py-3 font-mono text-xs uppercase tracking-[0.2em]",
                      isActive ? "text-cyan" : "text-dim"
                    )
                  }
                >
                  {l.label}
                  <ExternalLink className="h-3 w-3 opacity-40" aria-hidden="true" />
                </NavLink>
              ))}
              <div className="mt-4 flex items-center justify-between gap-3 pb-2">
                <a href={SITE.xUrl} target="_blank" rel="noopener noreferrer" data-testid="nav-mobile-x-link" className={cn(btnOutline, "px-4 py-2")}>
                  <XIcon /> X
                </a>
                <a href={SITE.buyUrl} target="_blank" rel="noopener noreferrer" data-testid="nav-mobile-buy-btn" className={cn(btnOutline, "px-4 py-2")}>
                  Buy
                </a>
                <button data-testid="nav-mobile-connect-btn" onClick={onConnect} className={cn(btnPrimary, "px-4 py-2")}>
                  {connectLabel}
                </button>
              </div>
              <div className="pb-2 pt-1">
                <StatusDot tone="success" label="Network online" />
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}

/** Thin system strip above the footer. */
export function SystemStrip() {
  return (
    <div className="border-y border-line bg-panel/70">
      <div className="container-t flex flex-wrap items-center justify-between gap-x-6 gap-y-1 py-2.5">
        <StatusDot tone="success" label="Verification layer online" />
        <MonoLabel className="hidden sm:block">Consequence Terminal · v1.0</MonoLabel>
        <MonoLabel className="text-[9px]">Proof of Consequence</MonoLabel>
      </div>
    </div>
  );
}
